// The theme catalogue (WEB_V2_BUILD.md §4): one record per theme, the single
// source for naples' tokens, the Look sheet's theme browser (exported as
// themeCatalogue.json by scripts/writeThemeCatalogue.mjs) and the studio.
// Values come from the themes canvas (T_*, T2_*, T3_* boards).
//
// A theme is tokens + slots. `legacy: true` themes keep their own component
// folder (glass, flat, nightwatch and the classic-* themes); every other theme
// renders the shared token-driven components in ./base.

export const STYLE_GROUPS = [
    {key: "clean", label: "Clean & modern"},
    {key: "warm", label: "Warm & natural"},
    {key: "editorial", label: "Editorial"},
    {key: "bold", label: "Bold & playful"},
    {key: "dark", label: "Dark & premium"},
    {key: "practical", label: "Practical"},
];

export const BUSINESS_TYPES = [
    {key: "trades", label: "Trades"},
    {key: "home", label: "Home & garden"},
    {key: "beauty", label: "Beauty & wellness"},
    {key: "food", label: "Food & drink"},
    {key: "creative", label: "Creative"},
    {key: "professional", label: "Professional"},
    {key: "shops", label: "Shops"},
    {key: "fitness", label: "Fitness"},
    {key: "kids-pets", label: "Kids & pets"},
];

// Old theme names keep working: everyone moves at release (§0), each to its
// closest new theme. The old look stays selectable as "Classic …" for 30 days.
export const ALIASES = {
    brutalist: "brutal",
    neon: "studio",
    corporate: "nightwatch",
    album: "editorial",
    paws: "fresh",
    bistro: "corner",
    noir: "studio",
    workwear: "workshop",
    shopfront: "corner",
};

export const CLASSIC = Object.keys(ALIASES).map((old) => ({
    name: `classic-${old}`,
    label: `Classic ${old.charAt(0).toUpperCase()}${old.slice(1)}`,
    folder: old,
    replacedBy: ALIASES[old],
}));

const SANS = "ui-sans-serif, system-ui, sans-serif";
const SERIF = "ui-serif, Georgia, serif";
const MONO = "ui-monospace, SFMono-Regular, monospace";

// Defaults every theme starts from; each theme lists only what differs.
export const BASE = {
    palette: {bg: "#ffffff", surface: "#ffffff", surface2: "#f5f5f4", ink: "#111827", muted: "#4b5563", line: "#e5e7eb", accent: "#2563eb"},
    type: {display: `'Public Sans', ${SANS}`, body: `'Public Sans', ${SANS}`, accent: null, displayWeight: 700, displayTracking: "-0.01em", displayCase: "none", leading: 1.6, h1: "clamp(2.25rem, 5vw, 3.75rem)", h2: "clamp(1.6rem, 3vw, 2.4rem)", h3: "1.25rem"},
    shape: {radius: "10px", radiusSm: "6px", radiusLg: "18px", border: "1px solid var(--t-line)", shadow: "0 10px 30px -18px rgba(0,0,0,.35)"},
    card: {bg: "var(--t-surface)", border: "1px solid var(--t-line)", shadow: "none", radius: "var(--t-radius)", padding: "1.25rem", clip: "none"},
    button: {bg: "var(--t-accent)", ink: "var(--t-accent-ink)", radius: "var(--t-radius-sm)", border: "0", case: "none", tracking: "0", weight: 600, shadow: "none", font: "var(--t-font-body)"},
    badge: {bg: "var(--t-accent)", ink: "var(--t-accent-ink)", radius: "4px", border: "0", case: "none", rotate: "0deg", font: "var(--t-font-body)"},
    photo: {radius: "var(--t-radius)", filter: "none", border: "0", shadow: "none", rotate: "0deg"},
    rhythm: {sectionPad: "clamp(3rem, 7vw, 6rem)", gap: "1.5rem", maxWidth: "1200px", altBg: "var(--t-surface-2)", bands: "alternate"},
    store: {perRow: 4, photoRatio: "1 / 1", card: "default", badge: "tag", button: "solid", basket: "drawer", priceWeight: 700, priceSize: "1rem", saleColour: "var(--t-accent)"},
    nav: {variant: "logo-left", menu: "auto"},
    footer: {kind: "columns"},
    hero: {layout: "full", allowed: ["full", "split", "framed"]},
    motion: "gentle",
};

// Font files per family (fontsource, latin subset, the weights the themes use).
export const FONT_FILES = {
    "Outfit": ["@fontsource/outfit/latin-400.css", "@fontsource/outfit/latin-600.css", "@fontsource/outfit/latin-700.css"],
    "Fraunces": ["@fontsource/fraunces/latin-400.css", "@fontsource/fraunces/latin-600.css"],
    "Work Sans": ["@fontsource/work-sans/latin-400.css", "@fontsource/work-sans/latin-600.css"],
    "Instrument Serif": ["@fontsource/instrument-serif/latin-400.css", "@fontsource/instrument-serif/latin-400-italic.css"],
    "Manrope": ["@fontsource/manrope/latin-400.css", "@fontsource/manrope/latin-600.css"],
    "Archivo Black": ["@fontsource/archivo-black/latin-400.css"],
    "Archivo": ["@fontsource/archivo/latin-400.css", "@fontsource/archivo/latin-700.css"],
    "Fredoka": ["@fontsource/fredoka/latin-500.css", "@fontsource/fredoka/latin-600.css"],
    "Nunito Sans": ["@fontsource/nunito-sans/latin-400.css", "@fontsource/nunito-sans/latin-700.css"],
    "Syne": ["@fontsource/syne/latin-700.css", "@fontsource/syne/latin-800.css"],
    "Karla": ["@fontsource/karla/latin-400.css", "@fontsource/karla/latin-700.css"],
    "Young Serif": ["@fontsource/young-serif/latin-400.css"],
    "Figtree": ["@fontsource/figtree/latin-400.css", "@fontsource/figtree/latin-700.css"],
    "Caveat": ["@fontsource/caveat/latin-600.css"],
    "Lora": ["@fontsource/lora/latin-400.css", "@fontsource/lora/latin-600.css"],
    "Jost": ["@fontsource/jost/latin-400.css", "@fontsource/jost/latin-500.css"],
    "Hanken Grotesk": ["@fontsource/hanken-grotesk/latin-400.css", "@fontsource/hanken-grotesk/latin-600.css"],
    "Gloock": ["@fontsource/gloock/latin-400.css"],
    "Instrument Sans": ["@fontsource/instrument-sans/latin-400.css", "@fontsource/instrument-sans/latin-600.css"],
    "DM Serif Display": ["@fontsource/dm-serif-display/latin-400.css"],
    "Inter Tight": ["@fontsource/inter-tight/latin-400.css", "@fontsource/inter-tight/latin-700.css"],
    "Schibsted Grotesk": ["@fontsource/schibsted-grotesk/latin-400.css", "@fontsource/schibsted-grotesk/latin-800.css"],
    "IBM Plex Mono": ["@fontsource/ibm-plex-mono/latin-400.css", "@fontsource/ibm-plex-mono/latin-500.css"],
    "Bagel Fat One": ["@fontsource/bagel-fat-one/latin-400.css"],
    "DM Sans": ["@fontsource/dm-sans/latin-400.css", "@fontsource/dm-sans/latin-700.css"],
    "Libre Bodoni": ["@fontsource/libre-bodoni/latin-600.css", "@fontsource/libre-bodoni/latin-600-italic.css"],
    "Albert Sans": ["@fontsource/albert-sans/latin-400.css", "@fontsource/albert-sans/latin-700.css"],
    "Lexend": ["@fontsource/lexend/latin-400.css", "@fontsource/lexend/latin-700.css"],
    "Marcellus": ["@fontsource/marcellus/latin-400.css"],
    "Red Hat Text": ["@fontsource/red-hat-text/latin-400.css", "@fontsource/red-hat-text/latin-500.css"],
    "Geist": ["@fontsource/geist/latin-400.css", "@fontsource/geist/latin-700.css"],
    "Geist Mono": ["@fontsource/geist-mono/latin-400.css", "@fontsource/geist-mono/latin-500.css"],
    "Anton": ["@fontsource/anton/latin-400.css"],
    "Space Mono": ["@fontsource/space-mono/latin-400.css", "@fontsource/space-mono/latin-700.css"],
    "Bricolage Grotesque Variable": ["@fontsource-variable/bricolage-grotesque/index.css"],
    "Public Sans": ["@fontsource/public-sans/latin-400.css", "@fontsource/public-sans/latin-700.css"],
    "Zilla Slab": ["@fontsource/zilla-slab/latin-400.css", "@fontsource/zilla-slab/latin-700.css"],
};

// ---- "Make it yours" (LOOK_CONTRACT.md): web_config.look ----
// The six global font pairings: one set for every non-legacy theme. Each is a
// display face plus a body face, both self-hosted (fonts.js has the loaders);
// only the chosen pairing loads. tokyo mirrors this list via themeCatalogue.json.
const pairing = (key, label, feel, display, displayWeight, body, extra = {}) => ({
    key, label, feel, display, displayWeight, body,
    displayTracking: extra.tracking ?? "-0.01em", displayCase: extra.case ?? "none",
    families: [...new Set([display, body])],
});
export const FONT_PAIRINGS = [
    pairing("classic", "Classic", "Serif headings, plain body", "Lora", 600, "Public Sans"),
    pairing("modern", "Modern", "Clean and precise", "Geist", 700, "Geist"),
    pairing("friendly", "Friendly", "Soft and rounded", "Fredoka", 600, "Nunito Sans", {tracking: "0"}),
    pairing("editorial", "Editorial", "High-contrast serif", "Libre Bodoni", 600, "Instrument Sans"),
    pairing("bold", "Bold", "Tall, condensed headings", "Anton", 400, "Archivo", {tracking: "0.01em"}),
    pairing("crafted", "Crafted", "Slab-serif, handmade feel", "Zilla Slab", 700, "Karla"),
];

// Allowed values for each control (null = the theme's own).
export const LOOK_OPTIONS = {
    backgrounds: ["tinted", "theme"],
    palette: ["light", "rich", "dark"],
    fonts: FONT_PAIRINGS.map((p) => p.key),
    corners: ["sharp", "soft", "round"],
    spacing: ["compact", "standard", "airy"],
    photos: ["natural", "rounded", "framed", "duotone"],
    hero: ["split", "centered", "full-photo", "text-only"],
    motion: ["off", "gentle", "lively"],
};

// The look's hero choices are named for people; the hero component's layouts
// (themes' hero.allowed) are named for the renderer. This is the bridge.
export const LOOK_HERO_LAYOUT = {split: "split", centered: "framed", "full-photo": "full", "text-only": "words"};

// Themes whose tint is capped at 3% besides the dark ones (data-theme-dark).
export const LOUD_THEMES = ["bold", "brutal", "pop", "retro"];

const f = (name, fallback = SANS) => `'${name}', ${fallback}`;

export const THEMES = [
    // ---- Kept as they are (legacy component folders) ----
    {
        name: "glass", label: "Glass", styleGroup: "clean", businessTypes: ["beauty", "shops", "creative"],
        feel: "Light, frosted, photo-led", suits: "Salons, beauty, wellness, gifts", legacy: true, kept: true,
        fonts: [],
        palette: {bg: "#f6eef4", surface: "rgba(255,255,255,.62)", surface2: "#efe7f3", ink: "#2a1f2d", muted: "#6a5a6f", line: "rgba(255,255,255,.9)", accent: "#8a3f7a"},
        card: {bg: "rgba(255,255,255,.62)", border: "1px solid #fff", shadow: "0 14px 30px -22px rgba(60,30,70,.6)", radius: "18px"},
        button: {radius: "999px", bg: "linear-gradient(135deg, var(--t-accent), color-mix(in srgb, var(--t-accent) 70%, #fff))"},
        badge: {radius: "999px"},
        photo: {radius: "12px"},
        store: {perRow: 3, photoRatio: "5 / 6", card: "frosted", badge: "pill", button: "pill", basket: "drawer", priceWeight: 800},
        nav: {variant: "classic"}, footer: {kind: "classic"}, hero: {layout: "full", allowed: ["full", "split", "framed"]},
    },
    {
        name: "flat", label: "Flat", styleGroup: "clean", businessTypes: ["trades", "professional", "shops"],
        feel: "As it is today", suits: "Existing Flat sites", legacy: true, kept: true, locked: true,
        fonts: [],
        palette: {ink: "#1f2937", muted: "#6b7280", accent: "#3b82f6"},
        card: {radius: "6px"},
        button: {radius: "4px"},
        badge: {radius: "3px"},
        photo: {radius: "4px"},
        store: {perRow: 4, photoRatio: "1 / 1", card: "default", badge: "tag", button: "solid", basket: "drawer", priceWeight: 600},
        nav: {variant: "classic"}, footer: {kind: "classic"}, hero: {layout: "full", allowed: ["full"]},
    },
    {
        name: "nightwatch", label: "Night Watch", styleGroup: "clean", businessTypes: ["professional"],
        feel: "Calm navy, trustworthy", suits: "Accountants, consultants, Draksy", legacy: true, kept: true,
        fonts: ["Bricolage Grotesque Variable", "Public Sans"],
        palette: {bg: "#e4edf5", surface: "#ffffff", surface2: "#f3f8fd", ink: "#10233e", muted: "#4b6480", line: "#c0d6e8", accent: "#17375d"},
        type: {display: f("Bricolage Grotesque Variable"), body: f("Public Sans"), displayWeight: 800},
        card: {border: "0", shadow: "0 4px 14px -6px rgba(16,35,62,.25)", radius: "8px", topRule: "4px solid #669ac4"},
        button: {radius: "8px"},
        badge: {bg: "#dff3ef", ink: "#155e56", radius: "999px"},
        photo: {radius: "4px"},
        store: {perRow: 3, photoRatio: "5 / 4", card: "paper", badge: "pill", button: "rounded", basket: "drawer", priceWeight: 800},
        nav: {variant: "classic"}, footer: {kind: "classic"}, hero: {layout: "full", allowed: ["full", "split", "words"]},
    },

    // ---- Clean & modern ----
    {
        name: "flat-reimagined", label: "Flat Reimagined", styleGroup: "clean", businessTypes: ["trades", "professional", "home"],
        feel: "Clean, bright, confident", suits: "IT, cleaning, services", fonts: ["Outfit"],
        palette: {bg: "#ffffff", surface: "#f3f7fd", surface2: "#eef4fc", ink: "#12233d", muted: "#5b6b82", line: "#dbe5f2", accent: "#2563c9"},
        type: {display: f("Outfit"), body: f("Outfit"), displayWeight: 700},
        card: {bg: "#f3f7fd", border: "0", radius: "14px"},
        button: {radius: "10px", weight: 700},
        badge: {bg: "#e8f0fc", ink: "var(--t-accent)", radius: "999px"},
        photo: {radius: "10px"},
        rhythm: {bands: "colour-blocks"},
        store: {perRow: 4, photoRatio: "1 / 1", card: "tinted", badge: "pill", button: "rounded", basket: "drawer", priceWeight: 800},
        nav: {variant: "logo-left"}, footer: {kind: "cta"}, hero: {layout: "split", allowed: ["split", "full", "words"]},
    },
    {
        name: "swiss", label: "Swiss", styleGroup: "clean", businessTypes: ["professional", "trades"],
        feel: "Strict, precise, clear", suits: "IT, engineering, consultancies", fonts: ["Geist", "Geist Mono"],
        palette: {bg: "#ffffff", surface: "#ffffff", surface2: "#f4f4f4", ink: "#111111", muted: "#666666", line: "#111111", accent: "#e3120b"},
        type: {display: f("Geist"), body: f("Geist"), accent: f("Geist Mono", MONO), displayWeight: 700, displayTracking: "-0.03em"},
        shape: {radius: "0", radiusSm: "0", radiusLg: "0"},
        card: {bg: "transparent", border: "0", radius: "0", padding: "0.75rem 0 0", topRule: "2px solid #111"},
        button: {radius: "0"},
        badge: {radius: "0", font: "var(--t-font-accent)"},
        photo: {radius: "0"},
        rhythm: {bands: "rules"},
        store: {perRow: 4, photoRatio: "1 / 1", card: "rule", badge: "square", button: "square", basket: "drawer", priceWeight: 600},
        nav: {variant: "logo-left"}, footer: {kind: "columns"}, hero: {layout: "words", allowed: ["words", "split"]},
    },

    // ---- Warm & natural ----
    {
        name: "workshop", label: "Workshop", styleGroup: "warm", businessTypes: ["trades", "home", "creative"],
        feel: "Warm, crafted, honest", suits: "Joiners, builders, makers, trades", fonts: ["Fraunces", "Work Sans"],
        palette: {bg: "#f7f1e6", surface: "#ffffff", surface2: "#efe5d3", ink: "#2c2218", muted: "#6d5e4e", line: "#e3d7c5", accent: "#8a4a1f"},
        type: {display: f("Fraunces", SERIF), body: f("Work Sans"), displayWeight: 600},
        shape: {radius: "0", radiusSm: "0"},
        card: {border: "0", radius: "0", clip: "polygon(0 0, 100% 0, 100% 92%, 90% 100%, 0 100%)"},
        button: {radius: "0", case: "uppercase", tracking: "0.06em"},
        badge: {bg: "transparent", ink: "var(--t-accent)", border: "1.5px solid var(--t-accent)", case: "uppercase"},
        photo: {radius: "0", border: "8px solid #fff", shadow: "0 14px 26px -16px rgba(60,40,20,.6)"},
        rhythm: {bands: "rules"},
        store: {perRow: 3, photoRatio: "4 / 5", card: "cut-corner", badge: "stamp", button: "square-caps", basket: "drawer", priceWeight: 600},
        nav: {variant: "logo-left"}, footer: {kind: "cta"}, hero: {layout: "split", allowed: ["split", "framed", "full"]},
    },
    {
        name: "nordic", label: "Nordic", styleGroup: "warm", businessTypes: ["shops", "home"],
        feel: "Airy, muted, Scandinavian", suits: "Homeware, textiles, interiors", fonts: ["Jost", "Hanken Grotesk"],
        palette: {bg: "#f4f0ea", surface: "#faf8f4", surface2: "#ece6dc", ink: "#3a3a34", muted: "#7a766c", line: "#ddd5c8", accent: "#6f7d63"},
        type: {display: f("Jost"), body: f("Hanken Grotesk"), displayWeight: 400, displayTracking: "0.04em"},
        shape: {radius: "2px", radiusSm: "0"},
        card: {bg: "transparent", border: "0", padding: "0"},
        button: {bg: "transparent", ink: "var(--t-ink)", border: "1px solid var(--t-ink)", radius: "0", case: "uppercase", tracking: "0.18em", weight: 500},
        badge: {bg: "transparent", ink: "var(--t-accent)", case: "uppercase"},
        photo: {radius: "0"},
        store: {perRow: 4, photoRatio: "4 / 5", card: "none", badge: "caps", button: "outline", basket: "drawer", priceWeight: 400},
        nav: {variant: "centred"}, footer: {kind: "columns"}, hero: {layout: "split", allowed: ["split", "full", "collage"]},
    },
    {
        name: "clay", label: "Clay", styleGroup: "warm", businessTypes: ["creative", "shops", "food"],
        feel: "Earthy, textured, handmade", suits: "Potters, florists, makers", fonts: ["Gloock", "Instrument Sans", "Caveat"],
        palette: {bg: "#f3efe6", surface: "#fbf8f2", surface2: "#ebe4d6", ink: "#3a3530", muted: "#7a6f63", line: "#d9cfbf", accent: "#a8522f"},
        type: {display: f("Gloock", SERIF), body: f("Instrument Sans"), accent: f("Caveat", "cursive"), displayWeight: 400},
        card: {bg: "transparent", border: "0", padding: "0"},
        button: {radius: "999px"},
        badge: {bg: "transparent", ink: "var(--t-accent)", font: "var(--t-font-accent)"},
        photo: {radius: "52% 48% 46% 54% / 46% 54% 46% 54%"},
        rhythm: {bands: "plain"},
        store: {perRow: 3, photoRatio: "1 / 1", card: "none", badge: "handwritten", button: "pill", basket: "drawer", priceWeight: 600},
        nav: {variant: "logo-left"}, footer: {kind: "local"}, hero: {layout: "split", allowed: ["split", "collage", "full"]},
    },
    {
        name: "calm", label: "Calm", styleGroup: "warm", businessTypes: ["beauty", "professional"],
        feel: "Soft, reassuring, spacious", suits: "Physios, therapists, clinics, carers", fonts: ["Lora", "Nunito Sans"],
        palette: {bg: "#f5f8f6", surface: "#ffffff", surface2: "#e9f1ed", ink: "#22312c", muted: "#56685f", line: "#d7e3dd", accent: "#3f7a68"},
        type: {display: f("Lora", SERIF), body: f("Nunito Sans"), displayWeight: 600, leading: 1.75},
        card: {border: "0", shadow: "0 10px 30px -22px rgba(30,60,50,.4)", radius: "18px"},
        button: {radius: "999px"},
        badge: {bg: "#e3eee9", ink: "#2f5d50", radius: "999px"},
        photo: {radius: "999px 999px 12px 12px"},
        rhythm: {sectionPad: "clamp(3.5rem, 8vw, 7rem)"},
        store: {perRow: 3, photoRatio: "4 / 5", card: "none", badge: "pill", button: "pill", basket: "drawer", priceWeight: 700},
        nav: {variant: "logo-left"}, footer: {kind: "local"}, hero: {layout: "split", allowed: ["split", "full", "words"]},
        motion: "none",
    },
    {
        name: "corner", label: "Corner Shop", styleGroup: "warm", businessTypes: ["food", "shops"],
        feel: "Local, cosy, welcoming", suits: "Cafés, bakeries, florists, shops", fonts: ["Young Serif", "Figtree", "Caveat"],
        palette: {bg: "#fbf5ec", surface: "#ffffff", surface2: "#f3e8d8", ink: "#2b2520", muted: "#6d6158", line: "#eadfcd", accent: "#c0532e"},
        type: {display: f("Young Serif", SERIF), body: f("Figtree"), accent: f("Caveat", "cursive"), displayWeight: 400},
        card: {border: "0", shadow: "0 6px 16px -10px rgba(60,40,20,.5)", radius: "2px", padding: "0.6rem 0.6rem 0.9rem"},
        button: {radius: "8px", weight: 700},
        badge: {bg: "#2b2520", ink: "#fbf5ec", font: "var(--t-font-accent)", rotate: "-4deg"},
        photo: {radius: "2px", rotate: "-1deg"},
        store: {perRow: 3, photoRatio: "1 / 1", card: "polaroid", badge: "chalkboard", button: "rounded", basket: "drawer", priceWeight: 700},
        nav: {variant: "centred"}, footer: {kind: "local"}, hero: {layout: "framed", allowed: ["framed", "split", "full"]},
    },

    // ---- Editorial ----
    {
        name: "editorial", label: "Editorial", styleGroup: "editorial", businessTypes: ["creative", "professional"],
        feel: "Magazine, white space, big serif", suits: "Photographers, architects, designers", fonts: ["Instrument Serif", "Manrope"],
        palette: {bg: "#fbfaf7", surface: "#ffffff", surface2: "#f3f1ec", ink: "#151412", muted: "#6b665d", line: "#151412", accent: "#9a3b2a"},
        type: {display: f("Instrument Serif", SERIF), body: f("Manrope"), displayWeight: 400, displayTracking: "-0.02em", h1: "clamp(3rem, 8vw, 6rem)"},
        shape: {radius: "0", radiusSm: "0"},
        card: {bg: "transparent", border: "0", padding: "0", radius: "0"},
        button: {bg: "transparent", ink: "var(--t-ink)", radius: "0", border: "0", weight: 600, underline: true},
        badge: {bg: "transparent", ink: "var(--t-accent)", case: "uppercase"},
        photo: {radius: "0"},
        rhythm: {bands: "rules", sectionPad: "clamp(4rem, 9vw, 8rem)"},
        store: {perRow: 3, photoRatio: "3 / 4", card: "none", badge: "caps", button: "text", basket: "drawer", priceWeight: 400},
        nav: {variant: "minimal"}, footer: {kind: "minimal"}, hero: {layout: "words", allowed: ["words", "collage", "full"]},
    },
    {
        name: "mono", label: "Mono", styleGroup: "editorial", businessTypes: ["creative", "professional"],
        feel: "Stark, huge type, thin rules", suits: "Architects, designers, consultants", fonts: ["Schibsted Grotesk", "IBM Plex Mono"],
        palette: {bg: "#fafaf8", surface: "#ffffff", surface2: "#f0f0ec", ink: "#111111", muted: "#777777", line: "#111111", accent: "#111111"},
        type: {display: f("Schibsted Grotesk"), body: f("IBM Plex Mono", MONO), displayWeight: 800, displayTracking: "-0.04em", h1: "clamp(3.5rem, 10vw, 8rem)", leading: 1.55},
        shape: {radius: "0", radiusSm: "0"},
        card: {bg: "transparent", border: "0", padding: "0", radius: "0"},
        button: {bg: "transparent", ink: "var(--t-ink)", radius: "0", weight: 500, underline: true},
        badge: {bg: "transparent", ink: "var(--t-ink)"},
        photo: {radius: "0", filter: "grayscale(1)"},
        rhythm: {bands: "rules"},
        store: {perRow: 4, photoRatio: "4 / 5", card: "numbered", badge: "brackets", button: "text", basket: "drawer", priceWeight: 500},
        nav: {variant: "minimal"}, footer: {kind: "minimal"}, hero: {layout: "words", allowed: ["words", "split"]},
        motion: "none",
    },
    {
        name: "retro", label: "Retro", styleGroup: "editorial", businessTypes: ["beauty", "shops", "food"],
        feel: "Grainy, '80s, nostalgic", suits: "Barbers, record shops, bars", fonts: ["Libre Bodoni", "Albert Sans"],
        palette: {bg: "#f1d3c2", surface: "#fbf1ea", surface2: "#ecc6b2", ink: "#2b1a1a", muted: "#6b4a44", line: "#2b1a1a", accent: "#a3324a"},
        type: {display: f("Libre Bodoni", SERIF), body: f("Albert Sans"), displayWeight: 600},
        shape: {radius: "0", radiusSm: "0"},
        card: {bg: "#fbf1ea", border: "1px solid #2b1a1a", radius: "0", doubleRule: true},
        button: {bg: "transparent", ink: "var(--t-ink)", radius: "0", border: "0", case: "uppercase", tracking: "0.16em", doubleRule: true},
        badge: {case: "none", radius: "0"},
        photo: {radius: "0", border: "4px solid #fff", rotate: "-1.5deg"},
        rhythm: {grain: true},
        store: {perRow: 3, photoRatio: "5 / 6", card: "double-rule", badge: "ribbon", button: "double-rule", basket: "drawer", priceWeight: 700},
        nav: {variant: "centred"}, footer: {kind: "local"}, hero: {layout: "framed", allowed: ["framed", "split", "full"]},
    },

    // ---- Bold & playful ----
    {
        name: "bold", label: "Bold", styleGroup: "bold", businessTypes: ["fitness", "trades"],
        feel: "Loud, chunky, energetic", suits: "Gyms, events, removals", fonts: ["Archivo Black", "Archivo"],
        palette: {bg: "#f4f1ea", surface: "#ffffff", surface2: "#111111", ink: "#111111", muted: "#444444", line: "#111111", accent: "#f5d90a"},
        type: {display: f("Archivo Black"), body: f("Archivo"), displayWeight: 400, displayCase: "uppercase", displayTracking: "-0.01em"},
        shape: {radius: "0", radiusSm: "0"},
        card: {border: "3px solid #111", radius: "0", padding: "1rem"},
        button: {bg: "#111", ink: "var(--t-accent)", radius: "0", case: "uppercase", font: "var(--t-font-display)", weight: 400},
        badge: {border: "2px solid #111", radius: "0", case: "uppercase", font: "var(--t-font-display)"},
        photo: {radius: "0"},
        rhythm: {bands: "colour-blocks"},
        store: {perRow: 4, photoRatio: "1 / 1", card: "hard-border", badge: "block", button: "block", basket: "page", priceWeight: 900, priceSize: "1.4rem"},
        nav: {variant: "logo-left"}, footer: {kind: "cta"}, hero: {layout: "words", allowed: ["words", "full", "split"]},
        motion: "lively",
    },
    {
        name: "fresh", label: "Fresh", styleGroup: "bold", businessTypes: ["kids-pets", "home"],
        feel: "Friendly, rounded, playful", suits: "Dog walkers, cleaners, nurseries, tutors", fonts: ["Fredoka", "Nunito Sans"],
        palette: {bg: "#fffaf3", surface: "#e3f6f2", surface2: "#fff1e0", ink: "#2b2b2b", muted: "#666666", line: "#e8e0d4", accent: "#2a9d8f"},
        type: {display: f("Fredoka"), body: f("Nunito Sans"), displayWeight: 600},
        shape: {radius: "24px", radiusSm: "14px", radiusLg: "32px"},
        card: {bg: "#e3f6f2", border: "0", radius: "24px"},
        button: {radius: "999px", weight: 700, shadow: "0 4px 0 color-mix(in srgb, var(--t-accent) 70%, #000)", font: "var(--t-font-display)"},
        badge: {bg: "#ff9f68", ink: "#fff", radius: "50%", rotate: "-12deg", font: "var(--t-font-display)"},
        photo: {radius: "44% 56% 48% 52% / 52% 44% 56% 48%"},
        store: {perRow: 3, photoRatio: "1 / 1", card: "pastel", badge: "sticker", button: "bouncy", basket: "drawer", priceWeight: 800},
        nav: {variant: "pill"}, footer: {kind: "cta"}, hero: {layout: "split", allowed: ["split", "full", "collage"]},
        motion: "lively",
    },
    {
        name: "pop", label: "Pop", styleGroup: "bold", businessTypes: ["kids-pets", "food"],
        feel: "Saturated, sticky, fun", suits: "Kids' parties, ice cream, play centres", fonts: ["Bagel Fat One", "DM Sans"],
        palette: {bg: "#fff6e8", surface: "#ffffff", surface2: "#ffe27a", ink: "#2a1b3d", muted: "#6a5a7a", line: "#2a1b3d", accent: "#ff3d8b"},
        type: {display: f("Bagel Fat One"), body: f("DM Sans"), displayWeight: 400},
        shape: {radius: "20px", radiusSm: "12px", radiusLg: "28px"},
        card: {border: "3px solid #2a1b3d", radius: "20px", shadow: "5px 5px 0 #2a1b3d"},
        button: {radius: "999px", border: "2px solid #2a1b3d", font: "var(--t-font-display)", weight: 400},
        badge: {bg: "#ffe27a", ink: "#2a1b3d", border: "2px solid #2a1b3d", radius: "50%", rotate: "10deg", font: "var(--t-font-display)"},
        photo: {radius: "12px"},
        rhythm: {bands: "colour-blocks"},
        store: {perRow: 4, photoRatio: "1 / 1", card: "offset-shadow", badge: "sticker", button: "chunky-pill", basket: "page", priceWeight: 800, priceSize: "1.25rem"},
        nav: {variant: "pill"}, footer: {kind: "cta"}, hero: {layout: "split", allowed: ["split", "words", "collage"]},
        motion: "lively",
    },
    {
        name: "brutal", label: "Brutal", styleGroup: "bold", businessTypes: ["fitness", "shops", "creative"],
        feel: "Raw, loud, unpolished", suits: "Skate, tattoo, gyms, streetwear", fonts: ["Anton", "Space Mono"],
        palette: {bg: "#efefea", surface: "#ffffff", surface2: "#000000", ink: "#000000", muted: "#333333", line: "#000000", accent: "#e10600"},
        type: {display: f("Anton"), body: f("Space Mono", MONO), displayWeight: 400, displayCase: "uppercase", displayTracking: "0", h1: "clamp(3.5rem, 11vw, 9rem)"},
        shape: {radius: "0", radiusSm: "0", radiusLg: "0"},
        card: {border: "3px solid #000", radius: "0", shadow: "6px 6px 0 #000"},
        button: {bg: "#000", ink: "#fff", radius: "0", case: "uppercase", weight: 700},
        badge: {radius: "0", rotate: "-4deg", font: "var(--t-font-display)"},
        photo: {radius: "0", border: "3px solid #000"},
        rhythm: {bands: "colour-blocks"},
        store: {perRow: 4, photoRatio: "1 / 1", card: "hard-shadow", badge: "tilted-block", button: "block", basket: "page", priceWeight: 700, priceSize: "1.25rem"},
        nav: {variant: "logo-left"}, footer: {kind: "columns"}, hero: {layout: "words", allowed: ["words", "collage", "full"]},
        motion: "lively",
    },

    // ---- Dark & premium ----
    {
        name: "studio", label: "Studio", styleGroup: "dark", businessTypes: ["beauty", "food", "creative"],
        feel: "Dark, cinematic, premium", suits: "Tattoo, barbers, bars, photographers", fonts: ["Syne", "Karla"],
        palette: {bg: "#0c0c0c", surface: "#161616", surface2: "#111111", ink: "#e9e6df", muted: "#8a8780", line: "#2a2a2a", accent: "#d4a24c"},
        type: {display: f("Syne"), body: f("Karla"), displayWeight: 800, displayCase: "uppercase", displayTracking: "0.02em"},
        shape: {radius: "0", radiusSm: "0"},
        card: {bg: "#161616", border: "0", radius: "0"},
        button: {bg: "transparent", ink: "var(--t-accent)", border: "1px solid var(--t-accent)", radius: "0", case: "uppercase", tracking: "0.14em"},
        badge: {bg: "transparent", ink: "var(--t-accent)", border: "1px solid var(--t-accent)", case: "uppercase", radius: "0"},
        photo: {radius: "0"},
        rhythm: {grain: true},
        store: {perRow: 3, photoRatio: "4 / 5", card: "dark", badge: "outline", button: "outline-caps", basket: "drawer", priceWeight: 500},
        nav: {variant: "minimal"}, footer: {kind: "minimal"}, hero: {layout: "full", allowed: ["full", "words", "collage"]},
        motion: "lively", dark: true,
    },
    {
        name: "cinema", label: "Cinema", styleGroup: "dark", businessTypes: ["creative", "food"],
        feel: "Dark, filmic, big serif", suits: "Venues, videographers, event spaces", fonts: ["DM Serif Display", "Inter Tight"],
        palette: {bg: "#0f0e0d", surface: "#1a1816", surface2: "#151311", ink: "#f1ece2", muted: "#9a9387", line: "#2c2925", accent: "#e6b450"},
        type: {display: f("DM Serif Display", SERIF), body: f("Inter Tight"), displayWeight: 400},
        card: {bg: "transparent", border: "0", padding: "0"},
        button: {radius: "6px", weight: 700},
        badge: {radius: "4px"},
        photo: {radius: "6px"},
        store: {perRow: 5, photoRatio: "2 / 3", card: "poster", badge: "chip", button: "solid", basket: "drawer", priceWeight: 600},
        nav: {variant: "logo-left"}, footer: {kind: "columns"}, hero: {layout: "full", allowed: ["full", "words"]},
        motion: "lively", dark: true,
    },
    {
        name: "prestige", label: "Prestige", styleGroup: "dark", businessTypes: ["trades", "home", "shops"],
        feel: "Silver, spaced, refined", suits: "Luxury kitchens, restorers, high-end trades", fonts: ["Marcellus", "Red Hat Text"],
        palette: {bg: "#0b0c0e", surface: "#14161a", surface2: "#101215", ink: "#e8e4dc", muted: "#8f8b84", line: "#2a2c30", accent: "#c8b07a"},
        type: {display: f("Marcellus", SERIF), body: f("Red Hat Text"), displayWeight: 400, displayCase: "uppercase", displayTracking: "0.14em"},
        shape: {radius: "0", radiusSm: "0"},
        card: {bg: "transparent", border: "0", padding: "0"},
        button: {bg: "transparent", ink: "var(--t-accent)", border: "1px solid var(--t-accent)", radius: "0", case: "uppercase", tracking: "0.22em", weight: 400},
        badge: {bg: "transparent", ink: "var(--t-accent)", case: "uppercase"},
        photo: {radius: "0"},
        rhythm: {sectionPad: "clamp(4rem, 9vw, 8rem)"},
        store: {perRow: 3, photoRatio: "3 / 4", card: "none", badge: "caps", button: "outline-caps", basket: "drawer", priceWeight: 300},
        nav: {variant: "split"}, footer: {kind: "columns"}, hero: {layout: "full", allowed: ["full", "words"]},
        motion: "gentle", dark: true,
    },

    // ---- Practical ----
    {
        name: "utility", label: "Utility", styleGroup: "practical", businessTypes: ["trades", "home"],
        feel: "Task-first, clear, trusted", suits: "Plumbers, electricians, heating engineers", fonts: ["Lexend"],
        palette: {bg: "#f3f6fb", surface: "#ffffff", surface2: "#e8eef7", ink: "#0d1f3c", muted: "#4f5f78", line: "#d5e0ef", accent: "#e8590c"},
        type: {display: f("Lexend"), body: f("Lexend"), displayWeight: 700, leading: 1.55},
        card: {border: "2px solid #d5e0ef", radius: "12px"},
        button: {radius: "8px", weight: 700},
        badge: {bg: "#0b3d91", ink: "#fff", radius: "4px"},
        photo: {radius: "8px"},
        store: {perRow: 4, photoRatio: "1 / 1", card: "bordered", badge: "block", button: "full-width", basket: "page", priceWeight: 800, priceSize: "1.25rem"},
        nav: {variant: "logo-left"}, footer: {kind: "columns"}, hero: {layout: "split", allowed: ["split", "full", "words"]},
        motion: "none",
    },
];

export const THEME_NAMES = THEMES.map((t) => t.name);
